import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { UserRole } from '@/types'
import { useAppDispatch } from '@store/index'
import { login, setUser } from '@store/slices/authSlice'
import AgencyHippaConsentDialog from './AgencyHippaConsentDialog'
import api from '@/services/api'

interface AgencyHippaConsentGuardProps {
  children: React.ReactNode
}

const AgencyHippaConsentGuard: React.FC<AgencyHippaConsentGuardProps> = ({ children }) => {
  const { user, token, refreshToken, role } = useAuth()
  const dispatch = useAppDispatch()
  const [showConsentDialog, setShowConsentDialog] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const [hasSignedHIPAADocusignAgreement, setHasSignedHIPAADocusignAgreement] = useState(false)


  useEffect(() => {
    const checkConsentStatus = async () => {
      if (role !== UserRole.AGENCY || !user) {
        setIsChecking(false)
        return
      }

      try {
        const response = await api.get('/agency/profile')
        const profileData = response?.data?.data || response?.data
        if (profileData) {
          const hasHippaSigned =
            profileData.hasSignedHIPAA ?? profileData.user?.hasSignedHIPAA
          setHasSignedHIPAADocusignAgreement(
            !!(
              profileData.hasSignedHIPAADocusignAgreement ??
              profileData.user?.hasSignedHIPAADocusignAgreement
            )
          )
          const userWithAgency = {
            ...user,
            ...(profileData.user || {}),
            agencyName: profileData.agencyName || user.agencyName,
            hasHippaSigned,
          }
          dispatch(setUser(userWithAgency))
          if (hasHippaSigned === false || hasHippaSigned === undefined) {
            setShowConsentDialog(true)
          }
        } else {
          if (user.hasHippaSigned === false || user.hasHippaSigned === undefined) {
            setShowConsentDialog(true)
          }
        }
      } catch (error) {
        console.error('Error fetching agency profile for HIPAA:', error)
        if (user.hasHippaSigned === false || user.hasHippaSigned === undefined) {
          setShowConsentDialog(true)
        }
      } finally {
        setIsChecking(false)
      }
    }

    checkConsentStatus()
  }, [role])

  const handleConsentSubmitted = async () => {
    if (!user || !token) return

    const updatedUser = { ...user, hasHippaSigned: true }

    dispatch(
      login({
        user: updatedUser,
        token,
        refreshToken: refreshToken || '',
      })
    )

    setShowConsentDialog(false)
  }

  if (isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Verifying consent status...
        </Typography>
      </Box>
    )
  }

  if (showConsentDialog && user) {
    return (
      <>
        <AgencyHippaConsentDialog
          open={showConsentDialog}
          user={user}
          hasSignedHIPAADocusignAgreement={hasSignedHIPAADocusignAgreement}
          onConsentSubmitted={handleConsentSubmitted}
        />
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
            pointerEvents: 'none',
          }}
        />
      </>
    )
  }

  return <>{children}</>
}

export default AgencyHippaConsentGuard
